<template>
  <div>
    <!-- Scan bar -->
    <b-form-group
      label="Référence de colis à ajouter au manifeste"
      label-for="colis-reference-input"
      aria-label="Champ de saisie de la référence du colis à ajouter au manifeste de sous-traitance."
      description="Saisissez ou scannez la référence du colis à ajouter au manifeste de sous-traitance."
    >
      <b-input-group class="input-group-merge">
        <b-input-group-prepend is-text>
          <feather-icon
            icon="MaximizeIcon"
            aria-hidden="true"
          />
        </b-input-group-prepend>
        <b-form-input
          id="colis-reference-input"
          v-model="ref"
          placeholder="Référence du colis (scannez ou saisissez)"
          aria-label="Entrée de la référence du colis à ajouter au manifeste"
          @change="checkOrder"
        />
      </b-input-group>
    </b-form-group>

    <div
      class="d-flex justify-content-between"
      aria-label="Sélection des colis pour le manifeste de sous-traitance"
    >
      <!-- LEFT PANEL: available orders -->
      <b-card aria-label="Colis disponibles pour le manifeste de sous-traitance">
        <b-form-group
          label="Société de sous-traitance"
          aria-label="Sélection de la société de sous-traitance"
          description="Choisissez la société pour filtrer les colis disponibles."
        >
          <v-select
            v-model="outsourcingCompany"
            :clearable="false"
            placeholder="Sélectionner une société"
            label="display_name"
            :options="Array.isArray(companies) ? companies : []"
            aria-label="Liste des sociétés de sous-traitance"
            @input="loadOrders"
          />
        </b-form-group>
        <span class="font-weight-bold">Nombre de colis disponibles : {{ orders.length }}</span>
        <vue-good-table
          :columns="columns"
          :rows="orders"
          :search-options="{ enabled: true, externalQuery: searchTerm }"
          aria-label="Tableau des colis disponibles pour le manifeste"
        >
          <template
            slot="table-row"
            slot-scope="props"
          >
            <span v-if="props.column.field === 'id'">
              {{ props.row.id }}
            </span>
            <span v-else-if="props.column.field === 'action'">
              <span>
                <feather-icon
                  :id="`manifest-row-${props.row.id}-add-icon`"
                  icon="CheckSquareIcon"
                  class="cursor-pointer mr-1"
                  size="16"
                  color="green"
                  aria-label="Ajouter ce colis au manifeste"
                  @click="addItem(props.row)"
                />
                <b-tooltip
                  title="Ajouter au manifeste"
                  class="cursor-pointer"
                  :target="`manifest-row-${props.row.id}-add-icon`"
                />
              </span>
            </span>
            <span v-else-if="props.column.field === 'governorate'">
              {{ props.row.governorate || '' }}
            </span>
            <span v-else>
              {{ props.formattedRow[props.column.field] }}
            </span>
          </template>
        </vue-good-table>
      </b-card>

      <!-- RIGHT PANEL: selected orders + form fields -->
      <b-card
        class="ml-1"
        aria-label="Colis sélectionnés pour le manifeste de sous-traitance"
      >
        <b-form-group
          label="Société sélectionnée"
          aria-label="Société de sous-traitance sélectionnée"
          description="La société à qui ce manifeste est destiné."
        >
          <b-form-input
            :value="outsourcingCompany ? (outsourcingCompany.display_name || outsourcingCompany.name) : ''"
            readonly
            placeholder="Aucune société sélectionnée"
          />
        </b-form-group>
        <span class="font-weight-bold">Nombre de colis sélectionnés : {{ selected.length }}</span>
        <vue-good-table
          :columns="columns"
          :rows="selected"
          :search-options="{ enabled: true, externalQuery: searchTerm }"
          aria-label="Tableau des colis sélectionnés pour le manifeste"
        >
          <template
            slot="table-row"
            slot-scope="props"
          >
            <span v-if="props.column.field === 'id'">
              {{ props.row.id }}
            </span>
            <span v-else-if="props.column.field === 'action'">
              <span>
                <feather-icon
                  :id="`selected-row-${props.row.id}-remove-icon`"
                  icon="XCircleIcon"
                  class="cursor-pointer mr-1"
                  size="16"
                  color="red"
                  aria-label="Retirer ce colis du manifeste"
                  @click="resetItem(props.row)"
                />
                <b-tooltip
                  title="Retirer du manifeste"
                  class="cursor-pointer"
                  :target="`selected-row-${props.row.id}-remove-icon`"
                />
              </span>
            </span>
            <span v-else-if="props.column.field === 'governorate'">
              {{ props.row.governorate || '' }}
            </span>
            <span v-else>
              {{ props.formattedRow[props.column.field] }}
            </span>
          </template>
        </vue-good-table>

        <!-- Livreur -->
        <b-form-group
          label="Livreur"
          aria-label="Sélection du livreur pour le manifeste"
          description="Sélectionnez le livreur affecté à ce manifeste."
        >
          <v-select
            v-model="deliveryMan"
            :clearable="false"
            placeholder="Sélectionner un livreur"
            label="username"
            :options="Array.isArray(deliveryMans) ? deliveryMans : []"
            aria-label="Liste des livreurs disponibles"
          />
        </b-form-group>

        <!-- Véhicule -->
        <b-form-group
          label="Véhicule"
          aria-label="Sélection du véhicule pour le manifeste"
          description="Sélectionnez le véhicule affecté à ce manifeste."
        >
          <v-select
            v-model="selectedCar"
            :clearable="false"
            placeholder="Choisir un véhicule"
            label="serial_number"
            :options="Array.isArray(cars) ? cars : []"
            :reduce="car => car.serial_number"
            aria-label="Liste des véhicules disponibles"
          />
        </b-form-group>

        <!-- Note -->
        <b-form-group label="Note">
          <b-form-textarea
            v-model="note"
            rows="2"
            placeholder="Note optionnelle..."
          />
        </b-form-group>

        <!-- Save button -->
        <div class="d-flex justify-content-end mt-2">
          <span id="save-manifest-btn-wrapper">
            <b-button
              ref="saveButton"
              variant="success"
              class="save-btn"
              :class="{ 'not-allowed': selected.length === 0 || !outsourcingCompany || !deliveryMan || !selectedCar }"
              aria-label="Créer le manifeste de sous-traitance avec les colis sélectionnés"
              :disabled="selected.length === 0 || !outsourcingCompany || !deliveryMan || !selectedCar"
              @click="createManifeste"
            >
              <feather-icon
                icon="SaveIcon"
                class="mr-1"
                aria-hidden="true"
              />
              Créer le manifeste de sous-traitance
            </b-button>
            <b-tooltip
              v-if="selected.length === 0 || !outsourcingCompany || !deliveryMan || !selectedCar"
              target="save-manifest-btn-wrapper"
              triggers="hover focus"
              placement="top"
            >
              <template v-if="selected.length === 0 && !outsourcingCompany && !deliveryMan && !selectedCar">
                Veuillez sélectionner une société, au moins un colis, un livreur et un véhicule.
              </template>
              <template v-else-if="selected.length === 0 && !outsourcingCompany">
                Veuillez sélectionner une société et au moins un colis.
              </template>
              <template v-else-if="!outsourcingCompany && !deliveryMan && !selectedCar">
                Veuillez sélectionner une société, un livreur et un véhicule.
              </template>
              <template v-else-if="selected.length === 0 && !deliveryMan">
                Veuillez sélectionner au moins un colis et un livreur.
              </template>
              <template v-else-if="selected.length === 0 && !selectedCar">
                Veuillez sélectionner au moins un colis et un véhicule.
              </template>
              <template v-else-if="!deliveryMan && !selectedCar">
                Veuillez sélectionner un livreur et un véhicule.
              </template>
              <template v-else-if="selected.length === 0">
                Veuillez sélectionner au moins un colis.
              </template>
              <template v-else-if="!outsourcingCompany">
                Veuillez sélectionner une société de sous-traitance.
              </template>
              <template v-else-if="!deliveryMan">
                Veuillez sélectionner un livreur.
              </template>
              <template v-else>
                Veuillez sélectionner un véhicule.
              </template>
            </b-tooltip>
          </span>
        </div>
      </b-card>
    </div>
  </div>
</template>

<script>
import {
  BCard,
  BButton,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BInputGroup,
  BInputGroupPrepend,
  BTooltip,
} from 'bootstrap-vue'
import { VueGoodTable } from 'vue-good-table'
import Ripple from 'vue-ripple-directive'
import axios from 'axios'
import { orderMatchesReference } from '@/utils/orderReference'
import vSelect from 'vue-select'
import storeAuth from '@/store/store'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  name: 'ManifestPickupCreate',
  components: {
    BCard,
    BButton,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BInputGroup,
    BInputGroupPrepend,
    BTooltip,
    VueGoodTable,
    vSelect,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      ref: '',
      orders: [],
      selected: [],
      companies: [],
      outsourcingCompany: null,
      deliveryMans: [],
      deliveryMan: '',
      cars: [],
      selectedCar: null,
      note: '',
      searchTerm: '',
      isSaving: false,
      user: {},
      columns: [
        {
          label: 'Action',
          field: 'action',
        },
        {
          label: 'code',
          field: 'id',
          filterOptions: {
            enabled: true,
            placeholder: 'chercher code',
          },
        },
        {
          label: 'nom',
          field: 'name',
          filterOptions: {
            enabled: true,
            placeholder: 'chercher nom',
          },
        },
        {
          label: 'gouvernorat',
          field: 'governorate',
          filterOptions: {
            enabled: true,
            placeholder: 'Chercher gouvernorat',
          },
        },
        {
          label: 'adresse',
          field: 'address',
          filterOptions: {
            enabled: true,
            placeholder: 'Chercher adresse',
          },
        },
        {
          label: 'tel',
          field: 'phone1',
          filterOptions: {
            enabled: true,
            placeholder: 'Chercher tel',
          },
        },
      ],
    }
  },
  created() {
    this.user = storeAuth.state.user
    this.getCompanies()
    this.getDeliveryMans()
    this.getCars()
  },
  methods: {
    async withTitle(text) {
      await this.$swal({
        title: 'Alerte',
        icon: 'warning',
        text,
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      })
      this.ref = ''
    },
    async getCompanies() {
      try {
        const { data } = await axios.get('/api/manifests/outsourcing-companies/')
        this.companies = data
      } catch (e) {
        this.showToast('danger', 'Erreur chargement sociétés.')
      }
    },
    async loadOrders() {
      this.selected = []
      this.orders = []
      if (!this.outsourcingCompany) return
      try {
        const { data } = await axios.get('/api/manifests/available-orders/', {
          params: { outsourcing_company_id: this.outsourcingCompany.id },
        })
        this.orders = data.results || data
      } catch (e) {
        this.showToast('danger', 'Erreur chargement colis.')
      }
    },
    async getDeliveryMans() {
      try {
        const { data } = await axios.get('/api/auth/users/drivers/')
        this.deliveryMans = data
      } catch (e) {
        this.showToast('danger', 'Erreur chargement livreurs.')
      }
    },
    async getCars() {
      try {
        const { data } = await axios.get('/api/cars/')
        this.cars = Array.isArray(data.results) ? data.results : []
      } catch (e) {
        this.showToast('danger', 'Erreur chargement véhicules.')
      }
    },
    checkOrder() {
      const searchRef = this.ref.trim()
      if (!searchRef) return

      const already = this.selected.find(order => orderMatchesReference(order, searchRef))
      if (already) {
        this.withTitle('Colis déjà ajouté au manifeste.')
        return
      }

      const order = this.orders.find(item => orderMatchesReference(item, searchRef))
      if (order) {
        this.addItem(order)
        this.ref = ''
      } else {
        this.withTitle('Colis introuvable ou non au dépôt.')
      }
    },
    addItem(item) {
      if (!this.outsourcingCompany) {
        this.withTitle("Veuillez d'abord sélectionner une société de sous-traitance.")
        return
      }
      if (this.selected.find(o => o.id === item.id)) return
      this.selected.push(item)
      this.orders = this.orders.filter(o => o.id !== item.id)
    },
    resetItem(item) {
      this.selected = this.selected.filter(o => o.id !== item.id)
      this.orders.push(item)
    },
    async createManifeste() {
      if (this.selected.length === 0 || !this.outsourcingCompany || !this.deliveryMan || !this.selectedCar) return
      this.isSaving = true
      try {
        const payload = {
          outsourcing_company_id: this.outsourcingCompany.id,
          order_ids: this.selected.map(o => o.id),
          note: this.note,
          driver_id: this.deliveryMan ? this.deliveryMan.id : null,
          car: this.selectedCar || null,
        }
        const { data } = await axios.post('/api/manifests/create/', payload)
        this.$toast({
          component: ToastificationContent,
          props: { title: 'Manifeste créé avec succès', icon: 'CheckIcon', variant: 'success' },
        })
        this.$router.push({ name: 'manifest-detail', params: { id: data.id } })
      } catch (err) {
        const msg = (err.response && err.response.data && err.response.data.detail) || 'Une erreur est survenue.'
        this.$toast({
          component: ToastificationContent,
          props: { title: msg, icon: 'AlertTriangleIcon', variant: 'danger' },
        })
      } finally {
        this.isSaving = false
      }
    },
    showToast(variant, message) {
      this.$toast({
        component: ToastificationContent,
        props: { title: message, icon: variant === 'success' ? 'CheckIcon' : 'AlertTriangleIcon', variant },
      })
    },
  },
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/vue-good-table.scss";

.save-btn.not-allowed,
.save-btn[disabled] {
  cursor: not-allowed !important;
  opacity: 0.6 !important;
  pointer-events: auto !important;
}
</style>
